import * as React from "react";
import {FontAwesomeIcon}        from "@fortawesome/react-fontawesome";
import {faChevronDown, faArrowRight} from "@fortawesome/free-solid-svg-icons";

import "./property-wizard.scss";

import { cs }                 from "@Common/react/chain-services";
import {UseState}             from "@Common/react/use-state";
import {Load2}                from "@Common/react/load2";
import {consumeContext}       from "@Common/react/context";
import {isDevXd}              from "@Common/ui-components/debug/is-dev";
import { ScreenLoader }       from "@Common/ui-components/loading-spinners/screen-loader";
import {AiSparklesSvg}        from "ai-banner/ai-sparkles-svg";
import {DebounceTimeout}      from "@Common/react/debounce-timeout";
import {ContextGatheringPage} from "./context-gathering-pg";
import {ResultsPage}          from "./results-pg";

export const PropertyWizard = ({ idOrder, item, idListing = null, listing = null, changeListingModal, isWizardFlyoutOpen, eligableContentFields, eligableSpecFields }) => cs(
    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("userLevels"),
    // Leaving these here for now for debugging. This will be removed before final release
    // TODO: Remove before release of product
    (({}, next) => next( console.log("eligableContentFields: ", eligableContentFields) )),
    (({}, next) => next( console.log("eligableSpecFields: ", eligableSpecFields) )),
    ["isDevModeOn", ({isInternalUser}, next) => next(isInternalUser && isDevXd())],
    ["propertyWizardData", ({apis}, next) => Load2({
        _key: isWizardFlyoutOpen.value,
        fetch: async () => {
            let results = await apis.next.gpt.getPropertyWizardData(idOrder);
            let summary = results?.aggregateSummary ?? null;
            let propertyDataFromDB = JSON.parse(results?.propertyData) ?? null;
            let propertyData = {
                event: "",
                beds: "",
                baths: "",
                squareFootage: "",
                price: "",
                description: "",
                additionalContext: "",
                street: "",
                line2: "",
                city: "",
                state: "",
                zip: ""
            };

            // If there is a listing applied override the default empty values
            if (listing && Object.keys(listing).length) {
                propertyData = {
                    event: listing?.event ?? "",
                    beds: listing?.bedrooms ?? "",
                    baths: listing?.bathrooms ?? "",
                    squareFootage: listing?.lotSizeSquareFeet ?? "",
                    price: listing?.listedPrice ?? "",
                    description: listing?.description ?? "",
                    additionalContext: "",
                    street: listing?.location?.streetAddress ?? "",
                    line2: listing?.location?.unitNumber ?? "",
                    city: listing?.location?.city ?? "",
                    state: listing?.location?.state ?? "",
                    zip: listing?.location?.zip ?? ""
                };
            }

            // Loop through results from DB
            // if there is a valid value in the property override the default value or value from listing
            if (propertyDataFromDB && Object.keys(propertyDataFromDB).length) {
                for (const key in propertyDataFromDB) {
                    if (propertyDataFromDB[key]?.length) {
                        propertyData[key] = propertyDataFromDB[key];
                    }
                }
            }
            return {...propertyData, aggregateSummary: summary};
        },
        captureException: true,
        next
    })],
    ["propertyData", ({propertyWizardData}, next) => UseState({
        _key: propertyWizardData.value,
        initValue: propertyWizardData?.value,
        next
    })],
    // Leaving these here for now for debugging. This will be removed before final release
    // TODO: remove these before release of product
    // ({}, next) => next( console.log("listing: ", listing) ),
    // ({propertyWizardData}, next) => next( console.log("propertyWizardData: ", propertyWizardData) ),
    // ({propertyData}, next) => next( console.log("propertyData: ", propertyData) ),
    ({}, next) => next( console.log("item: ", item) ),
    ["debounceTimeout", (_, next) => <DebounceTimeout {...{
        delay: 3000,
        next,
    }}/>],
    ["propertyDataChangeHandler", ({apis, propertyData, debounceTimeout}, next) => next((event) => {
        debounceTimeout.start();
        let {name, value} = event.target;
        propertyData.onChange({...propertyData.value, [name]: value});
    })],
    (({apis, debounceTimeout, propertyData}, next) => Load2({
        _key: debounceTimeout.active,
        fetch: async () => {
            if(!debounceTimeout.active && propertyData.value){
                await apis.next.gpt.savePropertyWizardData({
                    idOrder: parseInt(idOrder),
                    data: propertyData.value
                });
            }
        },
        next
    })),
    ["agregateTextualSummary", ({propertyWizardData}, next) => UseState({
        _key: propertyWizardData,
        initValue: propertyWizardData?.value?.aggregateSummary,
        next
    })],
    ["resultsGenerating", ({}, next) => UseState({
        initValue: false,
        next
    })],
    ["generateResults", ({apis, isDevModeOn, propertyWizardData, propertyData, resultsGenerating}, next) => next(async (data) => {
        event.preventDefault();
        resultsGenerating.onChange(true);
        let results = await apis.next.gpt.createPropertyAgregateSummary({
            idOrder: parseInt(item.itemId),
            data: propertyData.value
        });
        await propertyWizardData.reload();
        resultsGenerating.onChange(false);
        isDevModeOn && console.log("generateResults results: ", results);
    })],

    ({isDevModeOn, propertyData, propertyDataChangeHandler, resultsGenerating, generateResults, agregateTextualSummary, propertyWizardData}) => (
        <div className="property-wizard-b76">
            <div className="title">{AiSparklesSvg()}Generate AI Content</div>
            <div className="subtitle">Create professional, AI-Powered content for your marketing materials.</div>
            <hr className="title-footer-hr"/>
            {/* TODO: Replace loader with AI twinkle */}
            {propertyWizardData.loading ? <div className="page-loader"><ScreenLoader loaderColor="#6937B7"/></div>
            : !agregateTextualSummary.value?.length ?
                <ContextGatheringPage
                    idOrder={idOrder}
                    listing={listing}
                    changeListingModal={changeListingModal}
                    propertyData={propertyData}
                    propertyWizardData={propertyWizardData}
                    propertyDataChangeHandler={propertyDataChangeHandler}
                    generateResults={generateResults}
                    resultsGenerating={resultsGenerating.value}
                />

            :
            // TODO: Add some error handling in case there is an error value for agregateTextualSummary.value
                <ResultsPage
                    idOrder={idOrder}
                    item={item}
                    propertyData={propertyData}
                    propertyWizardData={propertyWizardData}
                    agregateTextualSummary={agregateTextualSummary}
                    eligableContentFields={eligableContentFields}
                    eligableSpecFields={eligableSpecFields}
                />
            }
        </div>
    )
);